import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../state/ModalSlice/ModalSlice.ts";
import { CircularProgress, IconButton, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from '@mui/material/InputAdornment';
import { toast } from 'react-toastify';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { db, SECRETS_ENV } from '../../../Config/firebase.ts';
import { getFutureCycles } from '../utilityFunctions.ts';
import { SubscriptionType } from '../../../enums.ts';
import { useBackend } from "../../../hooks/backend/useBackend.ts";
const secretCloudTaskToken = import.meta.env.VITE_SECRET_CLOUD_TASK_TOKEN || process.env.VITE_SECRET_CLOUD_TASK_TOKEN

interface Item {
    name: string;
    price: string;
    quantity: string;
}

// Professional currency formatting function
const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount).replace('₹', '₹ ');
};

export default function QuickCollectModal() {
    const Dispatch = useAppDispatch();
    const open = useAppSelector((state: RootState) => state.ModalSlice.QuickCollectModalOpen);
    const { subscriptionId, liveSubId } = useAppSelector((state: RootState) => state.ArbitrarySlice.ForQuickCollectModal);
    const ServiceProviderID = useAppSelector(selectEffectiveUID)
    const [subscriptionName, setSubscriptionName] = React.useState<string>("");
    const [studentName, setStudentName] = React.useState<string>("");
    const [subscriptionType, setSubscriptionType] = React.useState<string>("");
    const [selectedCycle, setSelectedCycle] = React.useState<string>("");
    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);
    const [cyclePriceArray, setCyclePriceArray] = React.useState<any>([]);
    const [currentPlanIndex, setCurrentPlanIndex] = React.useState<number>(0);

    const [items, setItems] = React.useState<Item[]>([
        { name: '', price: '', quantity: '1' }
    ]);

    // Error state for all items (using array indices)
    const [itemErrors, setItemErrors] = React.useState<{ name: boolean, price: boolean, quantity: boolean }[]>([
        { name: false, price: false, quantity: false }
    ]);

    // Close Modal Function
    function handleClose() {
        resetStates();
        Dispatch(closeModal("QuickCollectModalOpen"));
    }

    function resetStates() {
        setItems([{ name: '', price: '', quantity: '1' }]);
        setItemErrors([{ name: false, price: false, quantity: false }]);
    }

    // Calculate total for an item
    const calculateItemTotal = (price: string, quantity: string) => {
        const priceNum = parseFloat(price) || 0;
        const quantityNum = parseFloat(quantity) || 0;
        return priceNum * quantityNum;
    };

    // Calculate grand total
    const calculateGrandTotal = () => {
        return items.reduce((total, item) => {
            return total + calculateItemTotal(item.price, item.quantity);
        }, 0);
    };

    // Check if current item is complete
    const isItemComplete = (item: Item) => {
        return item.name.trim() !== '' && item.price.trim() !== '' && item.quantity.trim() !== '';
    };

    // Check if can add new item
    const canAddItem = () => {
        const lastItem = items[items.length - 1];
        return isItemComplete(lastItem);
    };

    // Update item field
    const updateItem = (index: number, field: keyof Item, value: string) => {
        setItems(prev => prev.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        ));
    };

    // Add new item
    const addItem = () => {
        if (canAddItem()) {
            setItems(prev => [...prev, { name: '', price: '', quantity: '1' }]);
            setItemErrors(prev => [...prev, { name: false, price: false, quantity: false }]);
        }
    };

    // Remove item
    const removeItem = (index: number) => {
        if (items.length > 1) {
            setItems(prev => prev.filter((_, i) => i !== index));
            setItemErrors(prev => prev.filter((_, i) => i !== index));
        }
    };

    // Handle price input (only numbers and decimal)
    const handlePriceChange = (index: number, value: string) => {
        if (/^\d*\.?\d*$/.test(value)) {
            updateItem(index, 'price', value);
        }
    };

    // Handle quantity input (positive numbers including decimals, not zero)
    const handleQuantityChange = (index: number, value: string) => {
        if (/^\d*\.?\d*$/.test(value) && value !== '0' && value !== '0.') {
            updateItem(index, 'quantity', value);
        }
    };

    // Error checking function
    const checkErrors = () => {
        let hasErrors = false;
        const newItemErrors: { name: boolean, price: boolean, quantity: boolean }[] = [];

        items.forEach((item, index) => {
            const errors = {
                name: item.name.trim() === '',
                price: parseFloat(item.price) < 6,
                quantity: parseFloat(item.quantity) < 1
            };

            if (errors.name || errors.price || errors.quantity) {
                hasErrors = true;
            }

            newItemErrors[index] = errors;
        });

        setItemErrors(newItemErrors);
        return hasErrors;
    };

    const { axiosPostRequest } = useBackend("");

    async function handleRaiseInvoiceAPICall() {
        Dispatch(toggleLoading(true));

        try {
            let response;
            if (subscriptionType === SubscriptionType.Count || subscriptionType === SubscriptionType.MultiMonth) {

                const multiMonthDoc = {
                    providerId: ServiceProviderID,
                    livesubscriptionId: liveSubId,
                    orderType: "online",
                    price: Number(calculateGrandTotal()),
                    recurring: false,
                    cycleName: selectedCycle,
                    subscriptionId: subscriptionId,
                    secretCloudTaskToken: secretCloudTaskToken,
                    billingCycle: subscriptionType === SubscriptionType.Count ? "Count" : cyclePriceArray[currentPlanIndex]?.billingCycle,
                    cyclePriceArray: cyclePriceArray,
                    currentPlanIndex: currentPlanIndex,
                    bQuickCollect: true,
                    billingStartTime: Timestamp.fromDate(new Date()),
                    billingEndTime: null,
                    items: items.map(item => ({
                        name: item.name,
                        price: Number(item.price),
                        quantity: Number(item.quantity)
                    })),

                };

                response = await axiosPostRequest(
                    `${SECRETS_ENV.API_END_POINT}/api/quickOrderMultiMonth`,
                    multiMonthDoc
                );

                if (response.message !== "Request successful") {
                    Dispatch(toggleLoading(false));
                    return "error";
                }

            } else if (subscriptionType === SubscriptionType.Monthly) {
                const body = {
                    providerId: ServiceProviderID,
                    livesubscriptionId: liveSubId,
                    orderType: "online",
                    chargeable: Number(calculateGrandTotal()),
                    recurring: false,
                    cycleName: selectedCycle,
                    secretCloudTaskToken: secretCloudTaskToken,
                    bQuickCollect: true,
                    items: items.map(item => ({
                        name: item.name,
                        price: Number(item.price),
                        quantity: Number(item.quantity)
                    })),
                }

                const response = await axiosPostRequest(`${SECRETS_ENV.API_END_POINT}/api/quickOrder`, body)
                if (response.message !== "Request successful") {
                    return "error"
                }
                Dispatch(toggleLoading(false));
                return "success";
            }
        } catch (error) {
            Dispatch(toggleLoading(false));
            return "error";
        } finally {
            Dispatch(toggleLoading(false));
        }
    }

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();

        // Run error checking
        const hasErrors = checkErrors();
        if (hasErrors) {
            toast.error("Please Fill the required fields");
            return;
        }

        const response = await handleRaiseInvoiceAPICall();
        if (response === "error") {
            toast.error("Error creating collection");
            return;
        } else {
            toast.success("Collection created successfully");
            handleClose();
        }



    }

    const grandTotal = calculateGrandTotal();


    React.useEffect(() => {
        const fetchData = async () => {
            if (subscriptionId && liveSubId) {
                try {
                    const subscriptionDocRef = doc(db, `ServiceProviders/${ServiceProviderID}/Subscriptions/${subscriptionId}`);
                    const liveSubDocRef = doc(db, `ServiceProviders/${ServiceProviderID}/LiveSubscriptions/${liveSubId}`);

                    const [subscriptionDoc, liveSubDoc] = await Promise.all([
                        getDoc(subscriptionDocRef),
                        getDoc(liveSubDocRef)
                    ]);

                    if (subscriptionDoc.exists() && liveSubDoc.exists()) {
                        const liveSubData = liveSubDoc.data();

                        if (liveSubData.subscriptionType === SubscriptionType.Count || liveSubData.subscriptionType === SubscriptionType.MultiMonth) {
                            setCyclePriceArray(liveSubData.assignedPlans);
                            setCurrentPlanIndex(liveSubData.assignedPlanIndex);
                        }

                        setSubscriptionName(liveSubData.subscriptionName || "");
                        setStudentName(liveSubData.studentName || "");
                        setSubscriptionType(liveSubData.subscriptionType);


                        const cycles = getFutureCycles();
                        // Get first two cycles
                        setSelectedCycle(cycles[0]);


                    }
                } catch (error) {
                    console.error("Error fetching data:", error);
                }
            }
        };

        fetchData();
    }, [ServiceProviderID, subscriptionId, liveSubId, open]);

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: '10px' }
            }}
        >
            <DialogContent sx={{ padding: "40px" }}>
                <div className="header pb-6">
                    <p className="text-xl font-semibold text-gray-900">Quick Collect</p>
                    <div className="absolute right-4 top-4">
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                </div>

                <div className="space-y-2">
                    {/* Customer Information */}
                    <div className='bg-gray-50 rounded-lg p-4 border border-gray-100'>
                        <p className="font-medium text-gray-900 mb-1">Student Name: {studentName}</p>
                        <p className="text-sm text-gray-600">Batch Name: {subscriptionName}</p>
                    </div>

                    <div className="space-y-4">
                        <div className="flex justify-between items-start">
                            <h3 className="text-lg font-semibold text-gray-900">Items</h3>
                        </div>

                        {items.map((item, index) => (
                            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                                <div className="flex justify-between items-center mb-3">
                                    <h4 className="font-medium text-gray-700 text-sm">Item {index + 1}</h4>
                                    <div className="flex items-center gap-4">
                                        {calculateItemTotal(item.price, item.quantity) > 0 && (
                                            <div className="text-right">
                                                <div className="bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg border border-blue-200">
                                                    <span className="text-sm font-semibold tabular-nums">
                                                        {formatCurrency(calculateItemTotal(item.price, item.quantity))}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        {items.length > 1 && (
                                            <button
                                                onClick={() => removeItem(index)}
                                                className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <TextField
                                        value={item.name}
                                        size='small'
                                        onChange={(e) => updateItem(index, 'name', e.target.value)}
                                        label="Item Name"
                                        variant="outlined"
                                        fullWidth
                                        placeholder="Enter item name"
                                        error={itemErrors[index]?.name || false}
                                        helperText={itemErrors[index]?.name ? "Item name is required" : ""}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: '#e5e7eb',
                                                    borderRadius: '8px'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#d1d5db',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#3b82f6',
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: '14px',
                                                },
                                            },
                                            fontWeight: 400,
                                        }}
                                    />

                                    <TextField
                                        value={item.price}
                                        size='small'
                                        onChange={(e) => handlePriceChange(index, e.target.value)}
                                        label="Price"
                                        variant="outlined"
                                        fullWidth
                                        placeholder="0.00"
                                        error={itemErrors[index]?.price || false}
                                        helperText={itemErrors[index]?.price ? "Minimum ₹6 required" : ""}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: '#e5e7eb',
                                                    borderRadius: '8px'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#d1d5db',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#3b82f6',
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: '14px',
                                                    fontVariantNumeric: 'tabular-nums',
                                                },
                                            },
                                            fontWeight: 400,
                                        }}
                                    />

                                    <TextField
                                        value={item.quantity}
                                        size='small'
                                        onChange={(e) => handleQuantityChange(index, e.target.value)}
                                        label="Quantity"
                                        variant="outlined"
                                        fullWidth
                                        placeholder="1.0"
                                        error={itemErrors[index]?.quantity || false}
                                        helperText={itemErrors[index]?.quantity ? "Minimum 1 required" : ""}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: '#e5e7eb',
                                                    borderRadius: '8px'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#d1d5db',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#3b82f6',
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: '14px',
                                                    fontVariantNumeric: 'tabular-nums',
                                                },
                                            },
                                            fontWeight: 400,
                                        }}
                                    />
                                </div>
                            </div>
                        ))}

                        <button
                            onClick={addItem}
                            disabled={!canAddItem()}
                            className={`w-full py-3 border-2 border-dashed rounded-lg text-sm font-medium transition-all ${canAddItem()
                                ? 'border-blue-200 text-blue-600 hover:border-blue-300 hover:bg-blue-50 cursor-pointer'
                                : 'border-gray-200 text-gray-400 cursor-not-allowed'
                                }`}
                        >
                            + Add Item
                        </button>
                    </div>

                    {/* Professional Total Section */}
                    {grandTotal > 0 && (
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-4">
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-sm text-gray-800 font-semibold tracking-wide">Total Amount</p>
                                    <p className="text-xs text-gray-800 mt-1">{items.length} item{items.length > 1 ? 's' : ''}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-2xl font-bold text-gray-900 tabular-nums">
                                        {formatCurrency(grandTotal)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={"flex gap-2 pt-4"}>
                        <button
                            onClick={handleClose}
                            disabled={isLoading}
                            className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onSubmit}
                            disabled={isLoading || grandTotal === 0}
                            className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading || grandTotal === 0 ? "cursor-not-allowed" : "cursor-pointer"}`}
                        >
                            {isLoading && <CircularProgress sx={{ color: "white" }} size={20} />}
                            <p className={"ml-2"}>
                                {isLoading ? "Creating..." : `Collect ${formatCurrency(grandTotal)}`}
                            </p>
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}